import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { SidebarSection } from "@/app-shell";
import { useApi } from "@/core/api";
import { agentTabs, loadingPages } from "./job-costing-nav";
import { reportOverviewOptions } from "./overview/query";
import { reportOutputOptions } from "./query";
import { templatePageTabs } from "./template-page/template-tabs";

/**
 * Which rows the column shows expanded. The one on screen is, unless it was
 * folded; any other, once its chevron opens it. Opening that row's page
 * forgets a fold left on it, so arriving there always shows its pages.
 */
export const useAgentExpansion = (currentId: string) => {
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [arrivedAt, setArrivedAt] = useState(currentId);
  if (arrivedAt !== currentId) {
    setArrivedAt(currentId);
    if (currentId in chosen) {
      const { [currentId]: _forgotten, ...rest } = chosen;
      setChosen(rest);
    }
  }
  return {
    isExpanded: (id: string) => chosen[id] ?? id === currentId,
    /** The other agents opened from the list, whose pages must be loaded. */
    opened: Object.keys(chosen).filter((id) => chosen[id] && id !== currentId),
    setExpanded: (id: string, expanded: boolean) =>
      setChosen((current) => ({ ...current, [id]: expanded })),
  };
};

/**
 * The pages of agents opened from the list (not the one on screen): each
 * one's latest period, read with the same requests and cache as its page.
 * Skeleton rows hold the pages until the period is in. The known pages show
 * then, and the script's panes join once its rows are in.
 */
export const useExpandedAgentPages = (
  businessId: string,
  reportIds: string[],
): Record<string, SidebarSection[] | undefined> => {
  const api = useApi();
  const overviews = useQueries({
    queries: reportIds.map((reportId) =>
      reportOverviewOptions(api, businessId, reportId),
    ),
  });
  const outputs = useQueries({
    queries: overviews.map((overview) => {
      const run = overview.data?.run;
      return reportOutputOptions(
        api,
        businessId,
        run?.interactive_available ? run.id : "",
      );
    }),
  });
  return Object.fromEntries(
    reportIds.map((reportId, index) => {
      const overview = overviews[index];
      const output = outputs[index];
      const run = overview.data?.run;
      if (overview.isPending) return [reportId, loadingPages(reportId)];
      const waitingForRows = !!run?.interactive_available && output.isPending;
      const { tabs } = templatePageTabs({
        output: waitingForRows ? undefined : output.data,
        work: overview.data?.review?.work ?? null,
        documents: !!run,
        activeCount: 0,
      });
      return [
        reportId,
        agentTabs({ reportId, tabs, current: null, search: "" }),
      ];
    }),
  );
};

/**
 * Starts loading an agent's latest period when the reader reaches for its
 * row, so its pages are usually in by the time its chevron is clicked. The
 * same requests as expanding it: fresh data is not asked for again.
 */
export const usePrefetchAgentPages = (businessId: string) => {
  const api = useApi();
  const client = useQueryClient();
  return (reportId: string) => {
    void client
      .ensureQueryData(reportOverviewOptions(api, businessId, reportId))
      .then((overview) => {
        const run = overview.run;
        if (run?.interactive_available)
          return client.prefetchQuery(
            reportOutputOptions(api, businessId, run.id),
          );
      })
      .catch(() => {
        // Expanding asks again and shows the failure where it belongs.
      });
  };
};
